import { isRedoEmailAddress } from "@redotech/account-common/redo-domains";
import {
  canOpenSettingsPages,
  type SettingsPageId,
} from "@redotech/redo-settings-common/settings-visibility/settings-pages";
import type { PermissionHandle } from "./protected-routes";

const GUARDED_PAGES = new WeakMap<object, readonly SettingsPageId[]>();

/** Route permission for a settings URL, taken from the registry pages it serves. */
export function settingsPagePermission(
  ...pages: [SettingsPageId, ...SettingsPageId[]]
): PermissionHandle {
  const handle: PermissionHandle = ({ user, userPermissions }) =>
    canOpenSettingsPages(pages, {
      permissions: userPermissions ?? new Set(),
      staff: isRedoEmailAddress(user.email),
    });
  GUARDED_PAGES.set(handle, pages);
  return handle;
}

/** Marks a wrapper permission as guarding the pages of the guards it delegates to. */
export function guardsSettingsPages(
  wrapper: PermissionHandle,
  delegates: readonly (PermissionHandle | undefined)[],
): void {
  const pages = delegates.flatMap((delegate) =>
    settingsPagesGuardedBy(delegate),
  );
  if (typeof wrapper === "function" && pages.length > 0) {
    GUARDED_PAGES.set(wrapper, pages);
  }
}

/** The registry pages a route permission guards, or none when it isn't from the registry. */
export function settingsPagesGuardedBy(
  handle: PermissionHandle | undefined,
): readonly SettingsPageId[] {
  if (typeof handle !== "function") {
    return [];
  }
  return GUARDED_PAGES.get(handle) ?? [];
}
